<script lang="ts" setup>
import useAuthService from '@/composables/apiServices/useAuthService';
import { useCookies } from '@vueuse/integrations/useCookies';
import dayjs from 'dayjs';
import { onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ROUTE_NAMES } from '@/router/conf';
import { useRouter } from 'vue-router';
import { useAuthChannel } from '@/composables/useAuthChannel';
import { EAuthMessages } from '@/types/composables/useAuthChannel.const';

const { t } = useI18n();
const router = useRouter();
const { getAccounts, refresh, logout, me } = useAuthService();
const { data: accounts } = getAccounts();
const { data: meData } = me();
const { mutateAsync: refreshUser, isPending: isRefreshing } = refresh();
const { mutateAsync: logoutUser } = logout();
const { get } = useCookies([]);
const { postPreRefresh, postAfterRefresh, postAfterLoggedOut, addAuthChannelEventListener } =
  useAuthChannel();

const expiredAccounts = ref<
  {
    uid: number;
    email: string;
  }[]
>([]);

let intervalId: ReturnType<typeof setInterval> | null = null;
let releaseLock: (() => void) | null = null;

navigator.locks.request('auth-refresh-leader', { mode: 'exclusive' }, () => {
  return new Promise<void>((resolve) => {
    releaseLock = resolve;
    intervalId = setInterval(
      async () => {
        if (!accounts.value?.length) {
          return;
        }
        const uids = [];
        const expiredAccs: {
          uid: number;
          email: string;
        }[] = [];
        for (const account of accounts.value) {
          const tokenExpiry = get('token_expiry_u_' + account.uid);
          if (!tokenExpiry) {
            expiredAccs.push({ uid: account.uid, email: account.email });
            continue;
          }
          const expiryDate = dayjs(tokenExpiry);

          if (
            expiryDate.diff(dayjs(), 'minutes') <=
            Number(import.meta.env.VITE_AUTH_EOL_POPUP_MINUTES_BEFORE || 60)
          ) {
            uids.push(account.uid);
          }
        }
        expiredAccounts.value = expiredAccs;
        if (!uids.length) {
          return;
        }
        postPreRefresh(uids);
        await Promise.all(uids.map((uid) => refreshUser({ userIndex: uid })));
        postAfterRefresh(uids);
      },
      Number(import.meta.env.VITE_AUTH_EOL_CHECK_INTERVAL_MINUTES || 15) * 60 * 1000
    );
  });
});

const handleLogout = async () => {
  await Promise.allSettled(
    expiredAccounts.value.map((account) =>
      logoutUser({ userIndex: account.uid }).then(() => postAfterLoggedOut(account.uid.toString()))
    )
  );

  const shouldRedirect =
    expiredAccounts.value.some((expired) => expired.email === meData.value?.email) ?? false;

  expiredAccounts.value = [];
  if (shouldRedirect) {
    await router.push({
      name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
      query: {
        reason: 'logOut'
      }
    });
  }
};

addAuthChannelEventListener((event) => {
  if (
    event.data.message === EAuthMessages.USER_LOGGED_OUT ||
    event.data.message === EAuthMessages.USER_REFRESHED
  ) {
    expiredAccounts.value = [];
  }
});
onUnmounted(() => {
  if (intervalId !== null) {
    clearInterval(intervalId);
  }
  releaseLock?.();
});
</script>

<template>
  <v-dialog persistent :model-value="!!expiredAccounts.length">
    <v-card>
      <v-card-title style="white-space: initial">
        {{
          expiredAccounts.length
            ? t('authTimeoutNotification.expiredTitle')
            : t('authTimeoutNotification.title')
        }}
      </v-card-title>
      <v-card-text>
        <p>
          {{ expiredAccounts.map((a) => a.email).join(', ') }}
        </p>
      </v-card-text>

      <v-card-actions>
        <TextBtn :disabled="isRefreshing" @click="handleLogout">
          {{ t('account.actions.logout') }}
        </TextBtn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped></style>
